import {Injectable} from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import {apiUrl} from "../../environments/environment";
import {map, Observable} from "rxjs";
import {CompanyRole, InviteUser, SubRole, EffectiveRole, User, UserLocation, UserWithSubRole} from "../_models/user";
import {Page} from "../_models/page";
import {convertEffectiveRoleToEnum} from "../_util/utils";
import {Trade, TradeType} from "../_models/trade";
import {Company} from "../_models/company";

@Injectable({
  providedIn: 'root'
})
export class UsersService {

  constructor(private http: HttpClient) { }

  getPrimaryCompany(user: User | null | undefined, preferredCompanyId?: number | null): Company | null {
    if (!user?.companies?.length) {
      return null;
    }

    if (preferredCompanyId) {
      return user.companies.find((company) => company.id === preferredCompanyId) ?? null;
    }

    return user.companies.at(0) ?? null;
  }

  getPrimaryCompanyId(user: User | null | undefined, preferredCompanyId?: number | null): number | null {
    return this.getPrimaryCompany(user, preferredCompanyId)?.id ?? null;
  }

  private normalizeCompanyRole(company: { companyRole?: CompanyRole } | null | undefined): void {
    if (!company) {
      return;
    }

    if (company.companyRole) {
      company.companyRole = CompanyRole[company.companyRole as keyof typeof CompanyRole] as CompanyRole;
    }
  }

  getUsersListByParams(request: UserListParams):Observable<Page<UserWithSubRole>> {
    let params = new HttpParams();
    if (request.userEffectiveRole) params = params.set('userEffectiveRole', request.userEffectiveRole);
    if (request.userSubRole) params = params.set('userSubRole', request.userSubRole);
    if(request.companyId) params = params.set('companyId', request.companyId);
    if(request.boardId) params = params.set('boardPublicId', request.boardId);
    if(request.projectId) params = params.set('projectId', request.projectId);
    if(request.tradeId) params = params.set('tradeId', request.tradeId);
    if(request.tradeType) params = params.set('tradeType', request.tradeType);
    if (request.onlyConfirmed) params = params.set('onlyConfirmed', request.onlyConfirmed);
    if (request.query && request.query.length > 0) params = params.set('query', request.query);
    if (request.sortField) params = params.set('sortField', request.sortField);
    if (request.sortDirection) params = params.set('sortDirection', request.sortDirection);
    if (request.page) params = params.set('page', request.page);
    if (request.pageSize) params = params.set('pageSize', request.pageSize);
    if (request.skipNotFoundOn403) params = params.set('SkipNotFoundNotification', 'true');

    return this.http.get<Page<UserWithSubRole>>(`${apiUrl}/user/list`, {params: params, withCredentials: true}).pipe(map(usersPage => {
      usersPage.content.forEach(e => {
        e.effectiveRole = convertEffectiveRoleToEnum(e.effectiveRole);
        e.user.effectiveRole = convertEffectiveRoleToEnum(e.user.effectiveRole);
        e.user.companies?.forEach((company) => this.normalizeCompanyRole(company));
      });
      return usersPage;
    }))
  }

  export(companyId: number, projectId: number, boardPublicId: string, tradeId: number,  tradeType: TradeType, userEffectiveRole: EffectiveRole,  userSubRole: SubRole, query: string, sortField: string, sortDirection: string) {
    let params = new HttpParams();
    if (userEffectiveRole) params = params.set('userEffectiveRole', userEffectiveRole);
    if (userSubRole) params = params.set('userSubRole', userSubRole);
    if(companyId) params = params.set('companyId', companyId);
    if(boardPublicId) params = params.set('boardPublicId', boardPublicId);
    if(projectId) params = params.set('projectId', projectId);
    if(tradeId) params = params.set('tradeId', tradeId);
    if(tradeType) params = params.set('tradeType', tradeType);
    if (query && query.length > 0) params = params.set('query', query);
    if (sortField) params = params.set('sortField', sortField);
    if (sortDirection) params = params.set('sortDirection', sortDirection);

    return this.http.get(`${apiUrl}/user/export`, {
      params: params,
      withCredentials: true,
      responseType: 'blob',
      observe: 'response'
    });
  }

  inviteUser(user: InviteUser) {
    return this.http.post(`${apiUrl}/user/invite`, user, {withCredentials: true})
  }

  getCompanyUsersNotInProject(projectId: number, tradeId?: number): Observable<User[]> {
    let params = new HttpParams();
    if (tradeId) params = params.set('tradeId', tradeId);
    return this.http.get<User[]>(`${apiUrl}/project/${projectId}/employee/notOnProject`, {params, withCredentials: true}).pipe(map(users => {
      users.forEach(e => e.effectiveRole = convertEffectiveRoleToEnum(e.effectiveRole));
      return users;
    }))
  }

  getCompanyUsersNotInBoard(boardId: number): Observable<User[]> {
    return this.http.get<User[]>(`${apiUrl}/colorBoard/${boardId}/employee/notOnBoard`, {withCredentials: true}).pipe(map(users => {
      users.forEach(e => e.effectiveRole = convertEffectiveRoleToEnum(e.effectiveRole));
      return users;
    }))
  }

  getConfirmedEmployeesOnProject(projectId: number, excludeViewers: boolean): Observable<User[]> {
    return this.http.get<User[]>(`${apiUrl}/project/${projectId}/employee/confirmed`, {params: {excludeViewers}, withCredentials: true}).pipe(map(users => {
      users.forEach(e => e.effectiveRole = convertEffectiveRoleToEnum(e.effectiveRole));
      return users;
    }))
  }

  getConfirmedEmployeesOnBoard(boardId: number): Observable<User[]> {
    return this.http.get<User[]>(`${apiUrl}/colorBoard/${boardId}/employee/confirmed`, {withCredentials: true}).pipe(map(users => {
      users.forEach(e => e.effectiveRole = convertEffectiveRoleToEnum(e.effectiveRole));
      return users;
    }))
  }

  getConfirmedEmployeesOnProjectShort(projectId: number, excludeViewers: boolean = false): Observable<User[]> {
    return this.http.get<User[]>(`${apiUrl}/project/${projectId}/employee/confirmed/short`, {params: {excludeViewers}, withCredentials: true});
  }

  getConfirmedEmployeesOnBoardShort(boardId: number): Observable<User[]> {
    return this.http.get<User[]>(`${apiUrl}/colorBoard/${boardId}/employee/confirmed/short`, {withCredentials: true});
  }

  getUserById(userId: number): Observable<User> {
    return this.http.get<User>(`${apiUrl}/user/${userId}`, {withCredentials: true}).pipe(map(user => {
      user.effectiveRole = convertEffectiveRoleToEnum(user.effectiveRole);
      user.companies?.forEach((company) => this.normalizeCompanyRole(company));
      return user;
    }))
  }

  getCurrentUser(options?: { skipCompanyScope?: boolean }): Observable<User> {
    const headers = options?.skipCompanyScope
      ? new HttpHeaders({'X-Skip-Company-Id': 'true'})
      : undefined;

    return this.http.get<User>(`${apiUrl}/user/me`, {withCredentials: true, headers}).pipe(map(user => {
      user.effectiveRole = convertEffectiveRoleToEnum(user.effectiveRole);
      user.companies?.forEach((company) => this.normalizeCompanyRole(company));
      return user;
    }))
  }

  getCurrentUserCompanies(): Observable<Company[]> {
    const headers = new HttpHeaders({'X-Skip-Company-Id': 'true'});

    return this.http.get<Company[]>(`${apiUrl}/user/companies`, {withCredentials: true, headers}).pipe(map(companies => {
      companies.forEach((company) => this.normalizeCompanyRole(company));
      return companies;
    }));
  }

  updateUserInfo(user: User, companyId?: number): Observable<User> {
    let params = new HttpParams();
    if (companyId) {
      params = params.set('companyId', companyId);
    }

    const body: UserInfoUpdate = {
      id: user.id,
      name: user.name,
      email: user.email,
      countryCode: user.countryCode,
      phone: user.phone,
      version: user.version,
    };

    return this.http.put<User>(`${apiUrl}/user`, body, {params, withCredentials: true}).pipe(map(response => {
      response.effectiveRole = convertEffectiveRoleToEnum(response.effectiveRole);
      response.companies?.forEach((company) => this.normalizeCompanyRole(company));
      return response;
    }))
  }

  updateCompanyUserRole(companyId: number, userId: number, role: CompanyRole): Observable<void> {
    return this.http.put<void>(`${apiUrl}/company/${companyId}/users/${userId}/role`, {role}, {withCredentials: true});
  }

  updateUserPassword(passwordInfo: any) {
    return this.http.put(`${apiUrl}/user/password`, null, {params:
          {oldPassword: passwordInfo.oldPassword, newPassword: passwordInfo.newPassword }, withCredentials: true})
  }

  resetPasswordSendEmail(email: string) {
    return this.http.post(`${apiUrl}/resetPassword`, null, {params: {email: email, SkipNotFoundNotification: 'true'}, withCredentials: true})
  }

  createNewPassword(code: string, password: string) {
    return this.http.post(`${apiUrl}/resetPassword/changePassword`, null, {params: {code: code, password: password}, withCredentials: true})
  }

  deleteUser(userIds: number[], companyId: number) {
    let params = new HttpParams();
    userIds.forEach(e => params = params.append('userIds', e));

    return this.http.delete(`${apiUrl}/company/${companyId}/users`,{
      params: params,
      observe: "response",
      withCredentials: true})
  }

  leaveCompany(companyId: number) {
    return this.http.delete(`${apiUrl}/company/${companyId}/users/me`, {
      observe: "response",
      withCredentials: true
    })
  }



  getUserHomePageUrl(user: User): string {
    try {
      switch (user.effectiveRole) {
        case EffectiveRole.ROLE_SUPER_ADMIN: return "/dashboard";
        case EffectiveRole.ROLE_USER: return `/company/${this.getPrimaryCompanyId(user)}/dashboard`;
        default: return `/company/${this.getPrimaryCompanyId(user)}/dashboard`
      }
    } catch {
      return `/login`
    }
  }

  // Super-admins have no company context, so they land on AdminModule's
  // top-level 'not-found' route (the actual 404 page) rather than a
  // company-scoped one.
  getUserNotFoundUrl(user: User): string {
    switch (user.effectiveRole) {
      case EffectiveRole.ROLE_SUPER_ADMIN: return "/not-found";
      default: return `/company/${this.getPrimaryCompanyId(user)}/not-found`
    }
  }

  setUserProjects(projectIds: number[], employeeId: number, version: number) {
    return this.http.put(`${apiUrl}/user/projects`, employeeId, {params: {projectIds: projectIds, version: version}, observe: "response", withCredentials: true})
  }

  //get user location

  getUserIp() {
    return this.http.get<{ip: string}>('https://api.ipify.org?format=json')
  }

  getUserLocationByIp(userIp: string) {
    return this.http.get<UserLocation>(`https://api.iplocation.net/?ip=${userIp}`)
  }
}

export interface UserListParams {
  companyId?: number;
  projectId?: number;
  boardId?: string;             // на сервер уходит как boardPublicId
  tradeId?: number;
  tradeType?: TradeType;
  userEffectiveRole?: EffectiveRole;
  userSubRole?: SubRole;
  onlyConfirmed?: boolean;
  query?: string;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
  skipNotFoundOn403?: boolean;
}

type UserInfoUpdate = Pick<User, 'id' | 'name' | 'email' | 'countryCode' | 'phone' | 'version'>;
